import { useContext, useEffect, useState } from "react";
import useLocales from "src/hooks/useLocales";
import { isValidToken } from "src/utils/jwt";

import ThemeContext from "../../Products/context";
import Context from "../context";
import useGetTimer from "./useGetTimer";
import useSelectServiceConnect, { ConnectAccountResponse } from "./useSelectServiceConnect";

type ConnectServiceState = {
	isLoading?: boolean;
	uniqId: string;
	totpNeeded?: boolean;
	userConfirmationNeeded?: boolean;
	isError?: boolean;
	isUnexpectedError?: boolean;
	errorMessage: string;
	isSuccess?: boolean;
	hasUpdated?: boolean;
};

export const CodeErrors = [
	"bad_credentials",
	"blocked_account",
	"credentials_exist",
	"missing_creds",
	"wrong_totp",
	"wrong_confirmation",
	"user_action_needed",
	"update_in_process",
	"wrong_confirmation",
	"entity_error",
	"restore_needed",
	"token_not_found",
	"invalid_session"
];

export default function useConnectAccount() {
	const [connectState, setConnectState] = useState<ConnectServiceState>({
		uniqId: "",
		errorMessage: "",
		isLoading: false,
		hasUpdated: false
	});

	const { entity, customConnectProps, product } = useContext(Context);
	const { state } = useContext(ThemeContext);

	const { translate } = useLocales();
	const { timeLeft } = useGetTimer({
		isReady: connectState.totpNeeded || connectState.userConfirmationNeeded
	});

	const { mutate } = useSelectServiceConnect();
	const isUpdate = customConnectProps?.updateToken;
	const isReset = customConnectProps?.restoreToken;
	const hasCredentials = entity && entity?.hasCredentials;
	const isBlocked = entity && entity?.blocked;
	const isUpdateFlow = (!!isUpdate && hasCredentials) || (!!isReset && isBlocked);

	useEffect(() => {
		if ((connectState.totpNeeded || connectState.userConfirmationNeeded) && timeLeft === 0) {
			setConnectState({
				...connectState,
				isError: true,
				errorMessage: translate(`connect_panel.expired_confirmation`, {
					entity: entity?.name
				}),
				totpNeeded: false,
				userConfirmationNeeded: false,
				isLoading: false,
				uniqId: ""
			});
		}
	}, [connectState.totpNeeded || connectState.userConfirmationNeeded, timeLeft]);

	useEffect(() => {
		if (
			entity &&
			entity.id !== "" &&
			isUpdateFlow &&
			!entity.maintenance &&
			!connectState.hasUpdated &&
			state.authToken
		) {
			setConnectState((prevState) => ({
				...prevState,
				hasUpdated: true
			}));
			connectRequest({ totp: "" }, () => {});
		}
	}, [entity, isUpdateFlow, state.authToken]);

	const onSuccess = () => {
		setConnectState({
			...connectState,
			isError: false,
			isUnexpectedError: false,
			errorMessage: "",
			isSuccess: true,
			isLoading: false
		});
	};

	const attemptSuccess = (data: ConnectAccountResponse) => {
		if (
			data.status &&
			(data.status === "totp_needed" || data.status === "user_confirmation_needed")
		) {
			setConnectState({
				...connectState,
				isLoading: false,
				isError: false,
				uniqId: data.uniqueId,
				errorMessage: "",
				totpNeeded: data.status === "totp_needed",
				userConfirmationNeeded: data.status === "user_confirmation_needed"
			});
		} else onSuccess();
	};

	const onError = ({ response }: any, reset: any) => {
		const { data } = response;
		let isUnexpectedError = true;
		let errorMessage = translate(
			isUpdateFlow ? "connect_panel.default_error_2" : "connect_panel.default_error"
		);

		if (response && data && data?.message && CodeErrors.includes(data.message)) {
			errorMessage = translate(`connect_panel.${data.message}`, { entity: entity?.name });
			isUnexpectedError = false;
		}
		reset({ totp: "" });
		setConnectState({
			...connectState,
			isError: true,
			errorMessage,
			uniqId: "",
			totpNeeded: false,
			userConfirmationNeeded: false,
			isLoading: false,
			isUnexpectedError
		});
	};

	const connectRequest = ({ totp, ...props }: any, reset: () => void) => {
		if (!isUpdateFlow && !isValidToken(state.authToken) && !product) {
			setConnectState({
				...connectState,
				isError: true,
				isUnexpectedError: true,
				errorMessage: translate("dynamic_form.expired_session"),
				totpNeeded: false,
				userConfirmationNeeded: false,
				isLoading: false
			});
		} else {
			setConnectState({
				...connectState,
				isSuccess: false,
				isError: false,
				isUnexpectedError: false,
				errorMessage: "",
				isLoading: true,
				hasUpdated: true
			});

			const entityId = entity?.id ?? "";

			let body: any = { entity: entityId };

			if (totp || connectState.userConfirmationNeeded) {
				body = {
					...body,
					totp: connectState.userConfirmationNeeded ? undefined : totp,
					uniqueId: connectState.uniqId,
					...props
				};
			} else {
				body = {
					...body,
					...props
				};
			}

			mutate(
				body,
				(data: ConnectAccountResponse) => attemptSuccess(data),
				(error: any) => onError(error, reset)
			);
		}
	};

	return {
		connectRequest,
		...connectState,
		isUpdateFlow
	};
}
